import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import WebUiPageWrapper from '../../components/page-wrapper';
import { useWebUiGlobalContext } from '../../context/global/use-global-context';
import { VlanAddIdPanel, VlanStatusPanel } from './vlan/vlan-panels';
import VlanPortSettingsTable from './vlan/vlan-port-settings-table';

type VlanMode = 'untagged' | 'tagged' | '-';
type Bridge = { vlan_id: number; name?: string };
type PortCfg = {
  default_vlan_id: number;
  discard_tagged: boolean;
  discard_untagged: boolean;
  force_default_vlan_id: boolean;
  vlan_map: Record<string, Exclude<VlanMode, '-'>>;
};

function sortPorts(ports: string[]): string[] {
  return ports.slice().sort((a, b) => {
    const na = Number((a.match(/\d+/) ?? ['9999'])[0]);
    const nb = Number((b.match(/\d+/) ?? ['9999'])[0]);
    if (na !== nb) return na - nb;
    return a.localeCompare(b);
  });
}

export default function WebUiSettingsVlanPage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [enabled, setEnabled] = React.useState(false);
  const [bridges, setBridges] = React.useState<Bridge[]>([]);
  const [portSettings, setPortSettings] = React.useState<
    Record<string, PortCfg>
  >({});
  const [ports, setPorts] = React.useState<string[]>([]);
  const [newVid, setNewVid] = React.useState(2);
  const [newVidName, setNewVidName] = React.useState('');
  const [removeVid, setRemoveVid] = React.useState(2);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const [global, bridgeData, settings] = await Promise.all([
      postWebUiApi<{ enabled?: boolean }>({
        service: 'vlan',
        access: 'get',
        command: 'get global vlans',
      }),
      postWebUiApi<unknown[]>({
        service: 'vlan',
        access: 'get',
        command: 'get bridges',
      }),
      postWebUiApi<Record<string, unknown>>({
        service: 'vlan',
        access: 'get',
        command: 'vlan port settings',
      }),
    ]);
    setEnabled(Boolean(global.enabled));
    setBridges((bridgeData as Bridge[]) ?? []);
    const next: Record<string, PortCfg> = {};
    Object.entries(settings ?? {}).forEach(([port, raw]) => {
      const value = (raw ?? {}) as Record<string, unknown>;
      next[port] = {
        default_vlan_id: Number(value.default_vlan_id ?? 1),
        discard_tagged: Boolean(value.discard_tagged),
        discard_untagged: Boolean(value.discard_untagged),
        force_default_vlan_id: Boolean(value.force_default_vlan_id),
        vlan_map: (value.vlan_map ?? {}) as Record<
          string,
          'untagged' | 'tagged'
        >,
      };
    });
    setPortSettings(next);
    setPorts(sortPorts(Object.keys(next)));
  }, []);

  React.useEffect(() => {
    reload().catch((err) => setError((err as Error).message));
  }, [reload]);

  const setGlobal = async () => {
    try {
      await postWebUiApi({
        service: 'vlan',
        access: 'set',
        command: 'set global vlans',
        parameters: { enable: enabled },
      });
      setMessage('VLAN status updated.');
      setError(null);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const addVid = async () => {
    try {
      await postWebUiApi({
        service: 'vlan',
        access: 'set',
        command: 'add vlan id',
        parameters: { vlan_id: newVid, name: newVidName },
      });
      setMessage('VLAN ID added.');
      setError(null);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const removeVidById = async (vid: number) => {
    try {
      setRemoveVid(vid);
      await postWebUiApi({
        service: 'vlan',
        access: 'set',
        command: 'remove vlan id',
        parameters: { vlan_id: vid },
      });
      setMessage(`VLAN ID ${vid} removed.`);
      setError(null);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const updatePorts = async () => {
    try {
      await postWebUiApi({
        service: 'vlan',
        access: 'set',
        command: 'update vlan port settings',
        parameters: portSettings,
      });
      setMessage('Port settings updated.');
      setError(null);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <WebUiPageWrapper titleList={['Settings', 'VLANs']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex w-full flex-row gap-2">
        <VlanStatusPanel
          readonly={readonly}
          enabled={enabled}
          setEnabled={setEnabled}
          onSet={setGlobal}
        />
        <VlanAddIdPanel
          readonly={readonly}
          newVid={newVid}
          setNewVid={setNewVid}
          newVidName={newVidName}
          setNewVidName={setNewVidName}
          onAdd={addVid}
        />
      </div>

      {enabled ? (
        <div className="flex w-full flex-col gap-2">
          <VlanPortSettingsTable
            ports={ports}
            bridges={bridges}
            portSettings={portSettings}
            readonly={readonly}
            onPortPatch={(port, patch) =>
              setPortSettings((prev) => ({
                ...prev,
                [port]: { ...prev[port], ...patch },
              }))
            }
            onMembershipChange={(port, vid, mode) =>
              setPortSettings((prev) => {
                const map = { ...(prev[port]?.vlan_map ?? {}) };
                if (mode === '-') delete map[String(vid)];
                else map[String(vid)] = mode;
                return { ...prev, [port]: { ...prev[port], vlan_map: map } };
              })
            }
            onRemoveVid={removeVidById}
          />
          <div className="flex w-full flex-row gap-2">
            <WebUiButton disabled={readonly} onClick={updatePorts}>
              Update Port Settings
            </WebUiButton>
            <WebUiButton
              disabled={readonly}
              onClick={() => removeVidById(removeVid)}
            >
              Remove VLAN ID
            </WebUiButton>
            <WebUiButton
              onClick={() =>
                reload().catch((err) => setError((err as Error).message))
              }
            >
              Reload
            </WebUiButton>
          </div>
        </div>
      ) : null}
      {!enabled ? (
        <div className="w-full text-center text-sm text-webui-muted">
          VLAN table is hidden when VLANs are disabled.
        </div>
      ) : null}
    </WebUiPageWrapper>
  );
}
